// Pure builders for the console map's sources and layers (testable without WebGL). Colours come from design tokens
// read at runtime; every layer is also told apart by shape, outline, dash or size, each has a legend entry with words,
// and incidents, alerts, stations and appliances get keyboard-reachable HTML labels, so colour is never the only
// signal. Red is used only for SOS and emergencies (critical incidents, SOS alerts).
import type { LayerSpecification } from "maplibre-gl";
import type { WardFeatureCollection } from "../api/types";

export type LngLat = [number, number];

export interface MapIncident {
  id: string;
  reference: string;
  severity: string;
  status: string;
  point: LngLat;
  accuracyM?: number | null;
  typeLabel: string;
  backup?: boolean;
}
export interface MapAlert {
  id: string;
  reference: string;
  channel: string;
  point: LngLat;
  accuracyM?: number | null;
}
export interface MapRefused {
  id: string;
  point: LngLat;
}
export interface MapStation {
  id: string;
  code: string;
  name: string;
  point: LngLat;
}
export interface MapAppliance {
  id: string;
  callSign: string;
  status: string;
  point: LngLat;
  /** Position older than the ETA age limit (or the home station stand-in). */
  stale: boolean;
}
export interface MapHydrant {
  id: string;
  code: string | null;
  condition: string;
  point: LngLat;
}
export interface MapFix {
  id: string;
  point: LngLat;
  accuracyM: number | null;
  label: string;
  chosen?: boolean;
}
export interface MapCandidate {
  id: string;
  kind: "incident" | "alert";
  reference: string;
  point: LngLat;
}

export interface MapData {
  incidents?: MapIncident[];
  alerts?: MapAlert[];
  refused?: MapRefused[];
  stations?: MapStation[];
  appliances?: MapAppliance[];
  hydrants?: MapHydrant[];
  wards?: WardFeatureCollection | null;
  fixes?: MapFix[];
  candidates?: MapCandidate[];
  /** A pin being dropped (intake). */
  pin?: LngLat | null;
  selectedId?: string | null;
}

export interface Palette {
  bg: string;
  surface: string;
  border: string;
  text: string;
  muted: string;
  primary: string;
  accent: string;
  warning: string;
  warningText: string;
  danger: string;
}

export const SOURCE_IDS = ["wards", "accuracy", "refused", "hydrants", "stations", "appliances", "candidates", "fixes", "alerts", "incidents", "pin"] as const;
export type SourceId = (typeof SOURCE_IDS)[number];

type FC = GeoJSON.FeatureCollection;
const fc = (features: GeoJSON.Feature[]): FC => ({ type: "FeatureCollection", features });
const point = (coordinates: LngLat, properties: Record<string, unknown>): GeoJSON.Feature => ({ type: "Feature", geometry: { type: "Point", coordinates }, properties });

/** A circle polygon of `radiusM` metres around `center` (accuracy rings; 32 segments). */
export function circle(center: LngLat, radiusM: number, segments = 32): GeoJSON.Polygon {
  const [lon, lat] = center;
  const dLat = radiusM / 111_320;
  const dLon = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  const ring: LngLat[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = (2 * Math.PI * i) / segments;
    ring.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return { type: "Polygon", coordinates: [ring] };
}

/** A refused SOS is a coarse point (0.01°): drawn as the 0.01° square it stands for, never a precise dot. */
export function coarseSquare(center: LngLat): GeoJSON.Polygon {
  const h = 0.005;
  const [lon, lat] = center;
  return { type: "Polygon", coordinates: [[[lon - h, lat - h], [lon + h, lat - h], [lon + h, lat + h], [lon - h, lat + h], [lon - h, lat - h]]] };
}

export function sourceData(d: MapData): Record<SourceId, FC> {
  const rings: GeoJSON.Feature[] = [
    ...(d.incidents ?? []).filter((i) => i.accuracyM && i.accuracyM > 0).map((i) => ({ type: "Feature" as const, geometry: circle(i.point, i.accuracyM!), properties: { kind: "incident" } })),
    ...(d.fixes ?? []).filter((f) => f.accuracyM && f.accuracyM > 0).map((f) => ({ type: "Feature" as const, geometry: circle(f.point, f.accuracyM!), properties: { kind: "fix" } })),
  ];
  return {
    wards: d.wards ? (d.wards as unknown as FC) : fc([]),
    accuracy: fc(rings),
    refused: fc((d.refused ?? []).map((r) => ({ type: "Feature", geometry: coarseSquare(r.point), properties: { id: r.id } }))),
    hydrants: fc((d.hydrants ?? []).map((h) => point(h.point, { id: h.id, condition: h.condition, working: h.condition === "working" }))),
    stations: fc((d.stations ?? []).map((s) => point(s.point, { id: s.id, code: s.code }))),
    appliances: fc((d.appliances ?? []).map((a) => point(a.point, { id: a.id, status: a.status, stale: a.stale }))),
    candidates: fc((d.candidates ?? []).map((c) => point(c.point, { id: c.id, kind: c.kind }))),
    fixes: fc((d.fixes ?? []).map((f) => point(f.point, { id: f.id, chosen: f.chosen === true }))),
    alerts: fc((d.alerts ?? []).map((a) => point(a.point, { id: a.id, sos: a.channel === "app_sos", selected: a.id === d.selectedId }))),
    incidents: fc(
      (d.incidents ?? []).map((i) =>
        point(i.point, { id: i.id, severity: i.severity, critical: i.severity === "critical", selected: i.id === d.selectedId }),
      ),
    ),
    pin: fc(d.pin ? [point(d.pin, { kind: "pin" })] : []),
  };
}

/** Radius by severity: size (not colour alone) tells severity apart; the HTML label says it in words. */
export const SEVERITY_RADIUS: Record<string, number> = { low: 6, medium: 8, high: 10, critical: 12 };

export function layerSpecs(p: Palette): LayerSpecification[] {
  return [
    { id: "wards-line", type: "line", source: "wards", paint: { "line-color": p.muted, "line-width": 1, "line-dasharray": [3, 2] } },
    { id: "accuracy", type: "fill", source: "accuracy", paint: { "fill-color": p.primary, "fill-opacity": 0.06, "fill-outline-color": p.muted } },
    // Refused SOS: a dashed-outline square (coarse area, not a point), no fill.
    { id: "refused", type: "line", source: "refused", paint: { "line-color": p.danger, "line-width": 2, "line-dasharray": [1, 1] } },
    {
      // Hydrants: small squares-ish circles; working filled, anything else hollow.
      id: "hydrants",
      type: "circle",
      source: "hydrants",
      paint: {
        "circle-radius": 4,
        "circle-color": ["case", ["get", "working"], p.accent, p.bg],
        "circle-stroke-color": p.text,
        "circle-stroke-width": 1,
      },
    },
    { id: "stations", type: "circle", source: "stations", paint: { "circle-radius": 7, "circle-color": p.primary, "circle-stroke-color": p.bg, "circle-stroke-width": 2 } },
    {
      // Appliances: available filled accent, dispatched filled black, out of service orange ring, off duty grey ring.
      id: "appliances",
      type: "circle",
      source: "appliances",
      paint: {
        "circle-radius": 6,
        "circle-color": [
          "match",
          ["get", "status"],
          "available",
          p.accent,
          "dispatched",
          p.primary,
          p.bg,
        ],
        "circle-stroke-color": ["match", ["get", "status"], "out_of_service", p.warningText, p.text],
        "circle-stroke-width": ["case", ["get", "stale"], 1, 3],
      },
    },
    { id: "candidates", type: "circle", source: "candidates", paint: { "circle-radius": 9, "circle-color": p.bg, "circle-opacity": 0, "circle-stroke-color": p.warningText, "circle-stroke-width": 2 } },
    {
      id: "fixes",
      type: "circle",
      source: "fixes",
      paint: { "circle-radius": ["case", ["get", "chosen"], 7, 4], "circle-color": p.bg, "circle-stroke-color": p.primary, "circle-stroke-width": 2 },
    },
    {
      // Unhandled alerts: hollow rings (SOS in red, others black), larger when selected.
      id: "alerts",
      type: "circle",
      source: "alerts",
      paint: {
        "circle-radius": ["case", ["get", "selected"], 10, 7],
        "circle-color": p.bg,
        "circle-stroke-color": ["case", ["get", "sos"], p.danger, p.primary],
        "circle-stroke-width": 3,
      },
    },
    {
      // Incidents: filled; radius by severity; critical in red (emergency), others black; selected gets a ring.
      id: "incidents",
      type: "circle",
      source: "incidents",
      paint: {
        "circle-radius": ["match", ["get", "severity"], "low", 6, "medium", 8, "high", 10, "critical", 12, 8],
        "circle-color": ["case", ["get", "critical"], p.danger, p.primary],
        "circle-stroke-color": ["case", ["get", "selected"], p.warningText, p.bg],
        "circle-stroke-width": ["case", ["get", "selected"], 4, 2],
      },
    },
    { id: "pin", type: "circle", source: "pin", paint: { "circle-radius": 8, "circle-color": p.bg, "circle-stroke-color": p.primary, "circle-stroke-width": 4 } },
  ];
}

/** Every point shown (for fitting the view). */
export function allPoints(d: MapData): LngLat[] {
  return [
    ...(d.incidents ?? []).map((x) => x.point),
    ...(d.alerts ?? []).map((x) => x.point),
    ...(d.fixes ?? []).map((x) => x.point),
    ...(d.candidates ?? []).map((x) => x.point),
    ...(d.pin ? [d.pin] : []),
  ];
}

export function bbox(points: LngLat[]): [LngLat, LngLat] | null {
  if (points.length === 0) return null;
  let [minX, minY] = points[0];
  let [maxX, maxY] = points[0];
  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

/** A point from a lon/lat pair of the API, or null when either is not a finite number within WGS84 range. */
export function pt(lon: unknown, lat: unknown): LngLat | null {
  return typeof lon === "number" && typeof lat === "number" && Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lon) <= 180 && Math.abs(lat) <= 90 ? [lon, lat] : null;
}
